import service from '@/utils/https';
import type { AxiosResponse } from 'axios';
import type { ApiResponse } from '@/types';
import type {
  ParseJobUrlRequest,
  ParseJobUrlResponse,
  ParseJobTextRequest,
  CLGenerationRequest,
  CLGenerationResponse,
  CLHistoryRequest,
  CLHistoryResponse,
  CoverLetterGenerationPrefillResponse,
} from '@/types/coverLetterGeneration';

/**
 * 解析职位 URL 获取 JD 信息
 * API: POST /v1/cl-generation/parse-job-url
 */
export function parseJobUrl(data: ParseJobUrlRequest): Promise<AxiosResponse<ApiResponse<ParseJobUrlResponse>>> {
  return service({
    url: '/v1/cl-generation/parse-job-url',
    method: 'post',
    data,
    timeout: 30 * 1000, // 30秒超时
  });
}

/**
 * 解析职位描述文本获取 JD 信息
 * API: POST /v1/cl-generation/parse-job-text
 *
 * 使用 AI 解析 JD 文本，自动提取职位名称、公司等信息
 * @param data.jd_text - 原始职位描述文本（最少 50 字符）
 */
export function parseJobText(data: ParseJobTextRequest): Promise<AxiosResponse<ApiResponse<ParseJobUrlResponse>>> {
  return service({
    url: '/v1/cl-generation/parse-job-text',
    method: 'post',
    data,
    timeout: 30 * 1000, // 30秒超时
  });
}

/**
 * 生成 Cover Letter
 * API: POST /v1/cl-generation/generate
 */
export function generateCoverLetter(data: CLGenerationRequest): Promise<AxiosResponse<ApiResponse<CLGenerationResponse>>> {
  return service({
    url: '/v1/cl-generation/generate',
    method: 'post',
    data,
    timeout: 180 * 1000, // 3分钟超时
  });
}

/**
 * 获取 Cover Letter 生成历史
 * API: GET /v1/cl-generation/history
 */
export function getCLGenerationHistory(params: CLHistoryRequest): Promise<AxiosResponse<ApiResponse<CLHistoryResponse>>> {
  return service({
    url: '/v1/cl-generation/history',
    method: 'get',
    params,
  });
}

/**
 * 存档 Cover Letter
 * API: PATCH /v1/cl-generation/{record_id}/archive
 */
export function archiveCLGeneration(recordId: number): Promise<AxiosResponse<ApiResponse<void>>> {
  return service({
    url: `/v1/cl-generation/${recordId}/archive`,
    method: 'patch',
  });
}

/**
 * 取消存档 Cover Letter
 * API: PATCH /v1/cl-generation/{record_id}/unarchive
 */
export function unarchiveCLGeneration(recordId: number): Promise<AxiosResponse<ApiResponse<void>>> {
  return service({
    url: `/v1/cl-generation/${recordId}/unarchive`,
    method: 'patch',
  });
}

/**
 * 下载 Cover Letter
 * API: GET /v1/cl-generation/{record_id}/download
 */
export function downloadCLGeneration(recordId: number): Promise<AxiosResponse<Blob>> {
  return service({
    url: `/v1/cl-generation/${recordId}/download`,
    method: 'get',
    responseType: 'blob',
  });
}

/**
 * 预览 Cover Letter PDF
 * API: GET /v1/cl-generation/{record_id}/preview
 *
 * 返回 PDF 用于浏览器内预览（Content-Disposition: inline）
 * 与 download 接口的区别：inline vs attachment
 */
export function previewCLGeneration(recordId: number): Promise<AxiosResponse<Blob>> {
  return service({
    url: `/v1/cl-generation/${recordId}/preview`,
    method: 'get',
    responseType: 'blob',
  });
}

/**
 * 获取 Cover Letter 生成详情
 * API: GET /v1/cl-generation/{record_id}
 */
export function getCLGenerationDetail(recordId: number): Promise<AxiosResponse<ApiResponse<CLGenerationResponse>>> {
  return service({
    url: `/v1/cl-generation/${recordId}`,
    method: 'get',
  });
}

/**
 * 更新 Cover Letter 内容（自动保存）
 * API: PUT /v1/cl-generation/{record_id}/content
 */
export function updateCLGenerationContent(recordId: number, content: string): Promise<AxiosResponse<ApiResponse<void>>> {
  return service({
    url: `/v1/cl-generation/${recordId}/content`,
    method: 'put',
    data: { content },
  });
}

/**
 * 重新生成 Cover Letter
 * API: POST /v1/cl-generation/regenerate/{record_id}
 */
export function regenerateCoverLetter(recordId: number, data: CLGenerationRequest): Promise<AxiosResponse<ApiResponse<CLGenerationResponse>>> {
  return service({
    url: `/v1/cl-generation/regenerate/${recordId}`,
    method: 'post',
    data,
    timeout: 180 * 1000, // 3分钟超时（与 generate 一致）
  });
}

/**
 * 获取 Cover Letter 生成预填写数据
 * API: GET /v1/prefill/cl-generation
 */
export function getCLGenerationPrefill(): Promise<AxiosResponse<ApiResponse<CoverLetterGenerationPrefillResponse>>> {
  return service({
    url: '/v1/prefill/cl-generation',
    method: 'get',
  });
}
